import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from '../components/Toast';
import { trackGuestOrder } from '../api/order.api';
import OrderStatusBadge from '../components/OrderStatusBadge';
import { resolveAssetUrl } from '../config';

const formatKES = (amount) => 'KES ' + Number(amount || 0).toLocaleString('en-KE', { minimumFractionDigits: 2 });

const TERMINAL_STATUSES = new Set([
    'delivered',
    'cancelled',
    'refunded',
    'returned',
    'payment_failed',
]);
const FAILED_STATUSES = new Set(['cancelled', 'payment_failed']);

export default function TrackOrder() {
    const { token: urlToken } = useParams();

    const [token, setToken] = useState(urlToken || '');
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searched, setSearched] = useState(false);

    useEffect(() => {
        document.title = 'Track Order | Fone Express';
    }, []);

    // Auto-track if token from URL
    useEffect(() => {
        if (urlToken) {
            handleTrack(urlToken);
        }
    }, [urlToken]);

    const handleTrack = async (trackToken) => {
        const t = (trackToken || token).trim();
        if (!t) {
            toast.error('Please enter a tracking token');
            return;
        }
        setLoading(true);
        setError(null);
        setOrder(null);
        setSearched(true);
        try {
            const res = await trackGuestOrder(t);
            const resData = res?.data || res;
            if (resData?.isOk && resData?.data) {
                setOrder(resData.data);
            } else {
                setError(resData?.message || 'Order not found. Please check your tracking token.');
            }
        } catch (err) {
            console.error('Error tracking order:', err);
            setError('Order not found. Please check your tracking token and try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        handleTrack();
    };

    return (
        <main className="pt-[10px] pb-12 min-h-screen bg-muted">
            <div className="max-w-[1400px] mx-auto px-4">
                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-foreground mb-2">Track Your Order</h1>
                    <p className="text-muted-foreground">
                        Enter your tracking token to see the status of your order
                    </p>
                </div>

                {/* Search Form */}
                <div className="max-w-xl mx-auto mb-8">
                    <form onSubmit={handleSubmit} className="flex gap-3">
                        <input
                            type="text"
                            value={token}
                            onChange={(e) => setToken(e.target.value)}
                            placeholder="Enter your tracking token"
                            className="flex-1 border border-border rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-primary bg-card"
                        />
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-6 py-3 bg-primary text-white rounded-lg font-medium hover:brightness-110 transition-all disabled:opacity-50 whitespace-nowrap"
                        >
                            {loading ? (
                                <i className="ri-loader-4-line animate-spin"></i>
                            ) : (
                                <>
                                    <i className="ri-search-line mr-1"></i>
                                    Track Order
                                </>
                            )}
                        </button>
                    </form>
                </div>

                {/* Loading */}
                {loading && (
                    <div className="max-w-2xl mx-auto">
                        <div className="bg-card border border-border rounded-lg p-6 animate-pulse space-y-4">
                            <div className="h-6 bg-muted rounded w-48"></div>
                            <div className="h-4 bg-muted rounded w-32"></div>
                            <div className="h-20 bg-muted rounded"></div>
                            <div className="h-20 bg-muted rounded"></div>
                        </div>
                    </div>
                )}

                {/* Error / Not Found */}
                {!loading && error && searched && (
                    <div className="max-w-2xl mx-auto text-center py-12">
                        <i className="ri-search-line text-5xl text-border mb-4 block"></i>
                        <h3 className="text-xl font-semibold text-foreground mb-2">Order Not Found</h3>
                        <p className="text-muted-foreground">{error}</p>
                    </div>
                )}

                {/* Order Info */}
                {!loading && order && (
                    <div className="max-w-2xl mx-auto space-y-6">
                        {/* Order Header */}
                        <div className="bg-card border border-border rounded-lg p-6">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                                <div>
                                    <h2 className="text-xl font-bold text-foreground">
                                        Order #{order.orderNumber}
                                    </h2>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        {new Date(order.createdAt).toLocaleDateString('en-KE', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric',
                                        })}
                                    </p>
                                </div>
                                <OrderStatusBadge status={order.status} />
                            </div>

                            {/* Status Timeline */}
                            {order.statusHistory && order.statusHistory.length > 0 && (
                                <div className="mt-4 pt-4 border-t border-border overflow-x-auto">
                                    <div className="flex items-start min-w-max">
                                        {order.statusHistory.map((entry, idx) => {
                                            const isLast = idx === order.statusHistory.length - 1;
                                            const isTerminal = TERMINAL_STATUSES.has(entry.status);
                                            const isFailed = FAILED_STATUSES.has(entry.status);
                                            const isComplete = !isLast || isTerminal;
                                            const statusLabel = (entry.status || '')
                                                .replace(/_/g, ' ')
                                                .replace(/\b\w/g, c => c.toUpperCase());
                                            return (
                                                <div key={idx} className="flex items-start flex-1 min-w-[100px]">
                                                    <div className="flex flex-col items-center">
                                                        <div
                                                            className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs ${
                                                                !isComplete
                                                                    ? 'bg-primary'
                                                                    : isFailed && isLast
                                                                      ? 'bg-red-500'
                                                                      : 'bg-green-500'
                                                            }`}
                                                        >
                                                            {!isComplete ? (
                                                                <i className="ri-time-line text-xs"></i>
                                                            ) : isFailed && isLast ? (
                                                                <i className="ri-close-line text-xs"></i>
                                                            ) : (
                                                                <i className="ri-check-line text-xs"></i>
                                                            )}
                                                        </div>
                                                        <p className="text-[10px] font-medium text-foreground mt-1 text-center">
                                                            {statusLabel}
                                                        </p>
                                                    </div>
                                                    {!isLast && (
                                                        <div className="flex-1 h-0.5 bg-green-500 mt-3 mx-1"></div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Items */}
                        <div className="bg-card border border-border rounded-lg">
                            <div className="p-4 border-b border-border">
                                <h3 className="font-semibold text-foreground">
                                    Items ({(order.items || []).length})
                                </h3>
                            </div>
                            <div className="divide-y divide-border">
                                {(order.items || []).map((item, idx) => (
                                    <div key={idx} className="p-4 flex gap-3">
                                        <div className="w-16 h-16 border border-border rounded-lg overflow-hidden bg-muted flex-shrink-0">
                                            <img
                                                src={resolveAssetUrl(item.image || item.productImage)}
                                                alt={item.productName || 'Product'}
                                                className="w-full h-full object-contain p-1"
                                            />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-foreground text-sm line-clamp-2">
                                                {item.productName}
                                            </p>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                Qty: {item.quantity}
                                            </p>
                                            <p className="text-sm font-semibold text-foreground mt-1">
                                                {formatKES(item.lineTotal || item.total)}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="p-4 border-t border-border flex justify-between items-center">
                                <span className="font-semibold text-foreground">Grand Total</span>
                                <span className="text-lg font-bold text-primary">
                                    {formatKES(order.grandTotal)}
                                </span>
                            </div>
                        </div>

                        {/* Shipping Info */}
                        {(order.shippingAddress || order.shipping?.estimatedDelivery || order.shipping?.trackingNumber) && (
                            <div className="bg-card border border-border rounded-lg p-6">
                                <h3 className="font-semibold text-foreground mb-4">Shipping Information</h3>
                                <div className="space-y-3 text-sm">
                                    {order.shippingAddress && (
                                        <div>
                                            <p className="text-muted-foreground mb-1">Delivery Address</p>
                                            <p className="text-foreground">
                                                {[
                                                    order.shippingAddress.city,
                                                    order.shippingAddress.county,
                                                ].filter(Boolean).join(', ')}
                                            </p>
                                        </div>
                                    )}
                                    {(order.delivery?.provider || order.shipping?.logisticsName || order.logisticsProvider) && (
                                        <div>
                                            <p className="text-muted-foreground mb-1">Delivery Partner</p>
                                            <p className="text-foreground font-medium">
                                                {order.delivery?.provider === 'fargo'
                                                    ? '🚚 Fargo Courier (Standard Delivery)'
                                                    : order.delivery?.provider === 'littlecab'
                                                        ? '⚡ LittleCab (Express Delivery)'
                                                        : order.delivery?.provider === 'manual'
                                                            ? '🚚 Delivery'
                                                            : order.shipping?.logisticsName || order.logisticsProvider}
                                            </p>
                                        </div>
                                    )}
                                    {(order.shipping?.trackingNumber || order.trackingNumber) && (
                                        <div>
                                            <p className="text-muted-foreground mb-1">Tracking Number</p>
                                            {(order.shipping?.trackingUrl || order.trackingUrl) ? (
                                                <a
                                                    href={order.shipping?.trackingUrl || order.trackingUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-primary hover:underline"
                                                >
                                                    {order.shipping?.trackingNumber || order.trackingNumber}
                                                    <i className="ri-external-link-line ml-1"></i>
                                                </a>
                                            ) : (
                                                <p className="text-foreground">{order.shipping?.trackingNumber || order.trackingNumber}</p>
                                            )}
                                        </div>
                                    )}
                                    {(order.shipping?.estimatedDelivery || order.estimatedDeliveryDate) && (
                                        <div>
                                            <p className="text-muted-foreground mb-1">Estimated Delivery</p>
                                            <p className="text-foreground">
                                                {new Date(order.shipping?.estimatedDelivery || order.estimatedDeliveryDate).toLocaleDateString('en-KE', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric',
                                                })}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Initial State - No search yet */}
                {!loading && !order && !error && !searched && (
                    <div className="max-w-2xl mx-auto text-center py-12">
                        <i className="ri-truck-line text-6xl text-border mb-4 block"></i>
                        <p className="text-muted-foreground">
                            Enter your tracking token above to get started
                        </p>
                    </div>
                )}
            </div>
        </main>
    );
}
